import { BreadcrumbGroup } from '@cloudscape-design/components';
import useGetUserById from 'features/user/hooks/useGetUserById';
import {
  useGetOrgById,
  useGetPartById,
} from 'hooks/use-basicQueries';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PartsBreadcrumbs({ pathnames }) {
  const navigate = useNavigate();

  const partId = useMemo(() => {
    if (pathnames.length === 0) {
      return null;
    }
    const category = pathnames[0];
    if (category === 'part' || category === 'parts') {
      return pathnames[1];
    }
    return null;
  }, [pathnames]);
  const { data: part } = useGetPartById(partId);

  const userId = useMemo(() => {
    if (part?.uid) {
      return part.uid;
    }
    if (pathnames.length === 2 && pathnames[0] === 'user') {
      return pathnames[1];
    }
    return null;
  }, [part?.uid, pathnames]);

  const { data: { email: userEmailFromId, organization: userOrgId } = {} } = useGetUserById(userId);
  const { data: { name: orgName } = {} } = useGetOrgById(userOrgId);

  const userEmail = useMemo(
    () => userEmailFromId || part?.email || userId,
    [part, userEmailFromId, userId],
  );

  const breadcrumbItems = useMemo(() => {
    if (pathnames.length === 0) {
      return [];
    }
    const category = pathnames[0];
    switch (category) {
      case 'part':
      case 'parts': {
        let breadcrumbs = [
          { text: 'Home', href: '/' },
          { text: 'Uploads', href: '/parts' },
        ];

        if (pathnames.length === 1) {
          return breadcrumbs;
        }
        breadcrumbs = [
          ...breadcrumbs,
          { text: `${userEmail}'s uploads`, href: `/user/${userId}` },
          { text: `Quote #${partId.substring(0, 6).toUpperCase()}`, href: `/part/${partId}` },
        ];
        if (orgName) {
          breadcrumbs.splice(2, 0, { text: orgName, href: `/organization/${userOrgId}` });
        }
        if (pathnames.length === 2) {
          return breadcrumbs;
        }
        switch (pathnames[2]) {
          case 'feedback': {
            return [
              ...breadcrumbs,
              { text: 'Feedback', href: `/part/${partId}/feedback` },
            ];
          }
          case 'pricing': {
            return [
              ...breadcrumbs,
              { text: 'Pricing', href: `/part/${partId}/pricing` },
            ];
          }
          case 'cad': {
            return [
              ...breadcrumbs,
              { text: 'CAD Studio', href: `/part/${partId}/cad` },
            ];
          }
          default: {
            return breadcrumbs;
          }
        }
      }
      case 'user': {
        if (userId) {
          const list = [
            { text: 'Home', href: '/' },
            { text: 'Uploads', href: '/parts' },
            { text: `${userEmail}'s uploads`, href: `/user/${userId}` },
          ];
          if (orgName) {
            list.splice(2, 0, { text: orgName, href: `/organization/${userOrgId}` });
          }
          return list;
        }
        return [];
      }
      default: {
        return [];
      }
    }
  }, [pathnames, userEmail, userId, partId, orgName, userOrgId]);

  if (breadcrumbItems.length === 0) {
    return null;
  }

  return (
    <BreadcrumbGroup
      onClick={(e) => {
        e.preventDefault();
        const { href } = e.detail;
        if (href) {
          navigate(href);
        }
      }}
      items={breadcrumbItems}
    />
  );
}
PartsBreadcrumbs.propTypes = {
  pathnames: PropTypes.arrayOf(PropTypes.string).isRequired,
};
